// Shared low-poly yard props — benches, tables, barrels, crates, fences and a
// veg patch. Ported from age-of-mcp's src/client/models/props.ts: the pure
// geometry is kept verbatim, but the game coupling is stripped — the Part type
// comes from the library, and the station-returning helpers (diningSeats) are
// dropped, since a model authors its stations through defineModel. All authored
// in footprint tile units (a 2x2 plot spans +/-1); y = 0 is the floor.
import * as THREE from "three";
import type { Part } from "../types";

const WOOD = "#7a5a34";
const WOOD_DARK = "#5e4528";
const BASE = 0.14;     // top of the standard thin foundation slab

// A bench: a seat plank on two stubby legs, `len` long along x.
export function bench(x: number, z: number, len = 0.46, color = WOOD): Part[] {
  const seatY = BASE + 0.12;
  return [
    { geometry: new THREE.BoxGeometry(len, 0.04, 0.14), color, position: [x, seatY, z] },
    { geometry: new THREE.BoxGeometry(0.04, 0.12, 0.12), color: WOOD_DARK, position: [x - len / 2 + 0.05, BASE + 0.06, z] },
    { geometry: new THREE.BoxGeometry(0.04, 0.12, 0.12), color: WOOD_DARK, position: [x + len / 2 - 0.05, BASE + 0.06, z] },
  ];
}

// A dining table: a top on a central trestle.
export function table(x: number, z: number, color = "#9a7440"): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(0.5, 0.05, 0.34), color, position: [x, BASE + 0.26, z] },
    { geometry: new THREE.BoxGeometry(0.1, 0.26, 0.26), color: WOOD_DARK, position: [x, BASE + 0.13, z] },
  ];
}

// A table flanked by two benches — one outdoor dining set facing along z.
const DINING_BENCH_DZ = 0.26;    // bench offset from the table centre

export function diningSet(x: number, z: number): Part[] {
  return [
    ...table(x, z),
    ...bench(x, z - DINING_BENCH_DZ),
    ...bench(x, z + DINING_BENCH_DZ),
  ];
}

// A barrel. `base` is the ground top it stands on.
export function barrel(x: number, z: number, base = BASE, h = 0.3, r = 0.13, color = "#8a5d2e"): Part {
  return { geometry: new THREE.CylinderGeometry(r, r * 0.92, h, 8), color, position: [x, base + h / 2, z] };
}

// A wooden crate.
export function crate(x: number, z: number, s = 0.26, color = "#9a7440"): Part {
  return { geometry: new THREE.BoxGeometry(s, s, s), color, position: [x, BASE + s / 2, z] };
}

// A single fence post.
export function fencePost(x: number, z: number, h = 0.3, color = WOOD_DARK): Part {
  return { geometry: new THREE.BoxGeometry(0.06, h, 0.06), color, position: [x, BASE + h / 2, z] };
}

// A low rail-fence enclosing the axis-aligned rectangle [x0..x1] x [z0..z1]:
// corner + midpoint posts and two horizontal rails per side. For a paddock or
// a garden border.
export function fenceRect(x0: number, z0: number, x1: number, z1: number, base = BASE, color = WOOD): Part[] {
  const out: Part[] = [];
  const postH = 0.3;
  const corners: [number, number][] = [
    [x0, z0], [x1, z0], [x0, z1], [x1, z1],
    [(x0 + x1) / 2, z0], [(x0 + x1) / 2, z1], [x0, (z0 + z1) / 2], [x1, (z0 + z1) / 2],
  ];
  for (const [px, pz] of corners) out.push({ geometry: new THREE.BoxGeometry(0.06, postH, 0.06), color: WOOD_DARK, position: [px, base + postH / 2, pz] });
  for (const ry of [base + 0.1, base + 0.22]) {
    out.push({ geometry: new THREE.BoxGeometry(Math.abs(x1 - x0), 0.03, 0.03), color, position: [(x0 + x1) / 2, ry, z0] });
    out.push({ geometry: new THREE.BoxGeometry(Math.abs(x1 - x0), 0.03, 0.03), color, position: [(x0 + x1) / 2, ry, z1] });
    out.push({ geometry: new THREE.BoxGeometry(0.03, 0.03, Math.abs(z1 - z0)), color, position: [x0, ry, (z0 + z1) / 2] });
    out.push({ geometry: new THREE.BoxGeometry(0.03, 0.03, Math.abs(z1 - z0)), color, position: [x1, ry, (z0 + z1) / 2] });
  }
  return out;
}

// A small kitchen-garden patch: a tilled soil square with rows of green tufts.
export function gardenPatch(cx: number, cz: number, base = BASE, half = 0.32): Part[] {
  const out: Part[] = [
    { geometry: new THREE.BoxGeometry(half * 2, 0.05, half * 2), color: "#6b4a2c", position: [cx, base + 0.025, cz] },
  ];
  for (const dz of [-half * 0.55, 0, half * 0.55]) {
    for (const dx of [-half * 0.5, 0, half * 0.5]) {
      out.push({ geometry: new THREE.ConeGeometry(0.04, 0.12, 5), color: "#5f9a43", position: [cx + dx, base + 0.1, cz + dz] });
    }
  }
  return out;
}

// A feed trough (a long open box) for a paddock / stable yard.
export function trough(x: number, z: number, color = WOOD_DARK): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(0.5, 0.1, 0.18), color, position: [x, BASE + 0.08, z] },
    { geometry: new THREE.BoxGeometry(0.46, 0.06, 0.14), color: "#caa53a", position: [x, BASE + 0.13, z] },
  ];
}
